<template>
  <main class="products-page">
    <div class="products-page__body" ref="body">
      <div class="products-page__row">
        <h1 class="products-page__title">Товары</h1>
        <div class="products-page__buttons">
          <button
            class="products-page__buttons-add products-page__buttons-add--product-category"
            type="button"
            v-on:click="openCategoryForm()">
            Добавить категорию товара
          </button>
          <button
            class="products-page__buttons-add products-page__buttons-add--product"
            type="button"
            v-on:click="openProductDialogForm">
            Добавить товар
          </button>
        </div>
      </div>
      <div class="products-page__categories">
        <input-text-search
          class="products-page__search"
          v-bind:value="searchQuery"
          v-on:input="onInputSearchQuery"
          placeholder="Введите название товара"
        />
        <multiple-autocomplete-input
          v-model="selectedCategories"
          chips
          item-text="name"
          item-value="id"
          label="Выберите категории"
          multiple
          removal-per-chip
          select-all-text="Все категории"
          set-select-all-option
          small-chips
          v-on:input="onSelectingCategory"
          v-bind:items="categories" />
      </div>
      <product-table
        v-bind:product-list="productList"
        v-bind:is-await-request="isAwaitRequest"
        v-bind:sort="sort"
        v-on:delete-product="onDeleteProduct"
        v-on:edit-product="fetchProduct"
        v-on:sort-product-list="fetchProductList"
      />
      <the-pagination v-model="page" v-bind:page-count="pageCount" />
    </div>
    <side-slide-transition>
      <confirm-side-dialog
        v-bind:label="productFormTitle"
        v-if="showProductDialogForm"
        v-on:cancel="closeProductDialogForm">
        <form-product
          v-bind:category-options="categories"
          v-bind:unit-options="unitOptions"
          v-bind:vendor-options="vendorOptions"
          v-bind:product-id="selectedProductId"
          v-bind:initial-name="selectedProduct.name"
          v-bind:initial-category="selectedProduct.category"
          v-bind:initial-manufacturer="selectedProduct.manufacturer"
          v-bind:initial-unit="selectedProduct.unit"
          v-bind:initial-vendor="selectedProduct.vendor"
          v-bind:initial-count-in-pack="selectedProduct.countInPack"
          v-bind:input-errors="inputErrors"
          v-on:create-category="openCategoryForm"
          v-on:create-unit="openUnitForm"
          v-on:submit="onSubmitProduct"
          v-on:close="closeProductDialogForm" />
      </confirm-side-dialog>
    </side-slide-transition>
    <modal-dialog
      content-class="products-page__modal"
      v-bind:title="dialogTitle"
      v-model="showModalDialog"
      v-on:input="closeModalDialog"
      with-cross
    >
      <form-product-category
        v-if="formName === $options.formCategory"
        v-bind:category-id="category.id"
        v-bind:initial-name="category.name"
        v-bind:input-errors="inputErrors"
        v-on:submit="onSubmitCategory"
        v-on:close="closeModalDialog" />
      <form-product-unit
        v-if="formName === $options.formUnit"
        v-bind:input-errors="inputErrors"
        v-bind:unit-id="unit.id"
        v-bind:initial-name="unit.name"
        v-on:submit="onSubmitUnit"
        v-on:close="closeModalDialog" />
    </modal-dialog>
  </main>
</template>

<script>
import { Product, ProductCategory, ProductUnit } from '@/storage/gateways/ProductGateway';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import ProductTable from '@/components/Warehouse/ProductTable.vue';
import ThePagination from '@/components/ThePagination.vue';
import FormProduct from '@/components/Warehouse/FormProduct.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import FormProductCategory from '@/components/Warehouse/FormProductCategory.vue';
import FormProductUnit from '@/components/Warehouse/FormProductUnit.vue';
import InputTextSearch from '@/components/InputTextSearch.vue';
import debounce from 'lodash.debounce';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';

/**
 * @description Примесь единиц измерения товаров.
 * @type {any}
 */
const unitMixin = {
  formUnit: 'unit',
  data() {
    return {
      unit: new ProductUnit(),
    };
  },
  methods: {
    /**
     * @description Открыть форму для работы с единицей измерения.
     * @returns {void}
     */
    async openUnitForm() {
      this.formName = this.$options.formUnit;
      this.openModalDialog();
    },
    /**
     * @description Обработать отправку единицы измерения.
     * @param {ProductUnitRequest} request
     */
    onSubmitUnit(request) {
      this.createProductUnit(request.toQuery());
    },
    /**
     * @description Создать единицу измерения товара.
     * @param {Object} params
     */
    createProductUnit(params) {
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.inputErrors = {};
        this.closeModalDialog();
        this.fetchOptions().then(() => {
          this.selectedProduct.unit = response.unit_id;
        });
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .createProductUnit(params)
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
};

/**
 * @description Примесь категорий товаров.
 * @type {any}
 */
const categoryMixin = {
  formCategory: 'category',
  data() {
    return {
      category: new ProductCategory(),
    };
  },
  methods: {
    /**
     * @description Обработать выбор категорий.
     * @param {array} categories
     */
    onSelectingCategory(categories) {
      this.selectedCategories = categories;
      this.fetchProductList(this.sort, this.selectedCategories);
    },
    /**
     * @description Открыть форму для работы с категорией.
     * @param {string|null} categoryId Категория.
     * @returns {void}
     */
    openCategoryForm(categoryId = null) {
      if (categoryId !== null) {
        this.category = this.categories.find((candidate) => candidate.id === categoryId);
      }
      this.formName = this.$options.formCategory;
      this.openModalDialog();
    },
    /**
     * @description Обработать удаление категории.
     * @param {string} categoryId
     */
    async onDeleteCategory(categoryId) {
      const dialog = await this
        .showRemoveDialog('Вы действительно хотите безвозвратно удалить категорию с товарами?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.fetchProductList(this.sort, this.selectedCategories);
        this.fetchOptions();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$productGateway
        .deleteProductCategory(categoryId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать отправку категории.
     * @param {ProductCategoryRequest} request
     */
    onSubmitCategory(request) {
      if (request.getId()) {
        this.editProductCategory(request);
      } else {
        this.createProductCategory(request);
      }
    },
    /**
     * @description Создать категорию товара.
     * @param {ProductCategoryRequest} request
     */
    createProductCategory(request) {
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.inputErrors = {};
        this.closeModalDialog();
        this.fetchOptions().then(() => {
          this.selectedProduct.category = response.category_id;
        });
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .createProductCategory(request.toQuery())
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Изменить категорию товара.
     * @param {ProductCategoryRequest} request
     */
    editProductCategory(request) {
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.inputErrors = {};
        this.closeModalDialog();
        this.fetchOptions();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .editProductCategory(request.getId(), request.toQuery())
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
};

/**
 * @description Страница списка товаров.
 * @type {Vue}
 */
export default {
  name: 'ClinicPanelWarehouseProductList',
  components: {
    MultipleAutocompleteInput,
    InputTextSearch,
    FormProductUnit,
    FormProductCategory,
    ModalDialog,
    ConfirmSideDialog,
    FormProduct,
    SideSlideTransition,
    ProductTable,
    ThePagination,
  },
  computed: {
    dialogTitle() {
      if (this.formName === this.$options.formCategory) {
        return this.category.id !== null ? 'Редактирование категории' : 'Добавление категории';
      }
      return 'Добавление единицы измерения';
    },
    /**
     * @description Заголовок формы товар.
     * @returns {string}
     */
    productFormTitle() {
      return this.selectedProductId ? 'Редактировать товар' : 'Добавить товар';
    },
  },
  created() {
    this.fetchOptions();
    this.fetchProductList({ type: 'name', order: 'asc' }, this.selectedCategories);
  },
  data() {
    return {
      productList: [],
      isAwaitRequest: false,
      sort: {},
      page: 1,
      pageCount: 0,
      formName: '',
      searchQuery: '',
      selectedProductId: '',
      selectedProduct: new Product(),
      selectedCategories: [],
      inputErrors: {},
      showProductDialogForm: false,
      isAwaitDownloadRequest: false,
      categories: [],
      unitOptions: [],
      vendorOptions: [],
      showModalDialog: false,
    };
  },
  methods: {
    /**
     * @description Создать товар.
     * @param {ProductRequest} request
     */
    createProduct(request) {
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.closeProductDialogForm();
        this.inputErrors = {};
        this.fetchProductList(this.sort, this.selectedCategories);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .create(request.toQuery())
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Закрыть диалоговое окно формы товара.
     * @returns {void}
     */
    closeProductDialogForm() {
      this.selectedProductId = '';
      this.selectedProduct = new Product();
      this.showProductDialogForm = false;
    },
    /**
     * @description Редактировать товар.
     * @param {ProductRequest} request
     */
    editProduct(request) {
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.closeProductDialogForm();
        this.inputErrors = {};
        this.fetchProductList(this.sort, this.selectedCategories);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$productGateway
        .edit(request.getId(), request.toQuery())
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Открыть диалоговое окно формы товаров.
     * @returns {void}
     */
    openProductDialogForm() {
      this.showProductDialogForm = true;
      this.inputErrors = {};
    },
    /**
     * @description Открыть модальное диалоговое окно.
     * @returns {void}
     */
    openModalDialog() {
      this.showModalDialog = true;
      this.inputErrors = {};
    },
    /**
     * @description Обработать ввод запроса.
     * @type {string} value
     * @returns {void}
     */
    onInputSearchQuery(value) {
      this.searchQuery = value;
    },
    /**
     * @description Закрыть модальное диалоговое окно.
     * @returns {void}
     */
    closeModalDialog() {
      this.formName = '';
      this.showModalDialog = false;
      this.category = new ProductCategory();
      this.unit = new ProductUnit();
    },
    /**
     * @description Получить список товаров.
     * @param {Object} sort       Сортировка.
     * @param {array} categories  Категории.
     * @returns {Promise<void>}
     */
    async fetchProductList(sort) {
      this.sort = sort;
      const onSuccess = ({ products, pageCount }) => {
        this.productList = products;
        this.pageCount = pageCount;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.isAwaitRequest = true;
      const either = await this.$productGateway.getAll(
        this.sort,
        this.page,
        this.selectedCategories,
        this.searchQuery,
      );
      either.fold(onFail, onSuccess);
      this.isAwaitRequest = false;
    },
    /**
     * @description Получить товар.
     * @param {string} productId Идентификатор товар.
     */
    async fetchProduct(productId) {
      const onSuccess = ({ product }) => {
        this.selectedProductId = productId;
        this.selectedProduct = product;
        this.openProductDialogForm();
      };
      const onFail = (error) => {
        this.selectedProductId = '';
        this.notifyFail(error.getTitle());
      };
      this
        .$productGateway
        .viewProduct(productId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Получить опции.
     * @returns {Promise}
     */
    fetchOptions() {
      const onSuccess = ({ categories, units, vendors }) => {
        this.categories = categories;
        this.unitOptions = units;
        this.vendorOptions = vendors;
      };
      const onFail = (error) => {
        this.selectedProductId = '';
        this.notifyFail(error.getTitle());
      };
      return this
        .$productGateway
        .viewOptions()
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать отправку запроса товар.
     * @param {ProductRequest} request
     */
    onSubmitProduct(request) {
      if (request.getId()) {
        this.editProduct(request);
      } else {
        this.createProduct(request);
      }
    },
    /**
     * @description Обработать событие удаления товар.
     * @param {number} id Идентификатор товар.
     * @returns {void}
     */
    async onDeleteProduct(id) {
      const dialog = await this
        .showRemoveDialog('Вы действительно хотите безвозвратно удалить товар?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.fetchProductList(this.sort, this.selectedCategories);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$productGateway
        .delete(id)
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
  mixins: [
    notifierMixin,
    builtInDialogMixin,
    categoryMixin,
    unitMixin,
  ],
  watch: {
    searchQuery: {
      handler: debounce(async function _() {
        this.page = 1;
        await this.fetchProductList({ type: 'name', order: 'asc' }, this.selectedCategories);
      }, 300),
    },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.v-snack__content {
  white-space: pre-line;
}

.products-page__modal {
  .v-card__title {
    margin-inline: auto;
    margin-bottom: 10px;
    max-width: 400px;
    word-break: auto-phrase;
  }
  .v-card__text {
    display: grid;
    gap: 15px;
    &__description {
      font-size: 16px;
    }
  }
}

.products {
  &-page {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    &__filters {
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 20px;
      &-label {
        color: gray;
        font-size: 12px;
      }
      &-additional {
        display: flex;
        gap:10px;
        margin-top: 20px;
      }
    }
    &__categories {
      max-width: 380px;
      &__caption {
        font-size: 14px;
        line-height: 17px;
        font-weight: 700;
        border-bottom: 2px solid #0489e0;
        padding: 10px;
      }
      &__container {
        display: flex;
        flex-direction: column;
        gap: 15px;
        width: 280px;
        background: #f2f7fa;
        padding: 20px 10px;
      }
    }
    &__buttons {
      display: grid;
      grid-template-columns: 1.2fr 1fr;
      max-width: 480px;
      width: 100%;
      column-gap: 10px;
      align-items: center;
      &-filter {
        margin-top: 25px;
        display: flex;
        align-items: center;
        gap: 5px;
        max-width: 175px;
        width: 100%;
        color: #1F85C8;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        >img {
          width: 15px;
          height: 15px;
        }
      }
      &-add {
        @include buttons.btn-blue(100%, 48px, 16px);
        @include buttons.btn-blue-add(19px);
      }

      &-export {
        @include buttons.btn-transparent(100%, 48px, 16px);

        &:before {
          content: "";
          background-image: url("@/assets/img/download-file-blue.svg");
          background-repeat: no-repeat;
          background-size: contain;
          width: 20px;
          height: 20px;
          margin: 0 8px 0 0;
        }

        &:disabled {
          color: var(--unfocus-color);
          border-color: var(--unfocus-color);
          background-color: transparent;
        }
      }

      &-delete {
        @include buttons.btn-blue(50%, 48px, 16px);
        width: 100%;
      }

      &-delete:disabled {
        background-color: var(--unfocus-color);
      }
    }

    &__body {
      padding: 38px 20px 48px 23px;
      width: 100%;
      overflow-y: scroll;
    }

    &__search {
      margin-bottom: 20px;
      max-width: 400px;
    }

    &__row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      margin-bottom: 10px;
    }
  }
}

@media screen and (max-width: 960px) {
  .products {
    &-page {
      flex-direction: column;

      &__row {
        flex-direction: column;
        gap: 10px;
      }
    }
  }
}

@media screen and (max-width: 640px) {
  .products {
    &-page {
      &__body {
        padding: 38px 10px 140px;
        width: 100%;
        overflow-y: scroll;
      }

      &__buttons {
        grid-template-columns: 1.2fr 1fr;
        max-width: 100%;
        row-gap: 10px;
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .v-text-field__slot input::placeholder {
    font-size: 12px;
  }
  .products-page {
    &__title {
      margin-bottom: 15px;
    }
    &__buttons {
      grid-template-columns: minmax(250px, 300px);
    }
    &__buttons-add {
      &--product-category {
        order: 1;
      }
      &--product {
        order: 0;
      }
    }
  }
}
@media screen and (max-width: 400px) {
  .v-card__actions {
    .dialog__button {
      width: 100%;
    }
  }
}
</style>
